import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Activity, CheckCircle, Users, TrendingUp, ArrowRight,
  FileText, FileCheck, Receipt, DollarSign, Bell, Send,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart,
  PieChart, Pie, Cell,
} from 'recharts';

const fmtCurrency = (v) =>
  (v || 0).toLocaleString('fr-FR').replace(/,/g, ' ') + ' FCFA';

const fmtShort = (v) => {
  const n = Math.abs(v || 0);
  if (n >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + 'Md';
  if (n >= 1_000_000) return (v / 1_000_000).toFixed(0) + 'M';
  if (n >= 1_000) return (v / 1_000).toFixed(0) + 'K';
  return String(v || 0);
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return { date, time };
  } catch {
    return { date: '', time: '' };
  }
};

const KpiCard = ({ icon: Icon, iconBg, label, value, delta, deltaLabel, deltaColor, testid }) => (
  <Card className="border border-slate-200 shadow-sm" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
          <div className="flex items-center gap-1 mt-1.5">
            <span className={`text-sm font-semibold ${deltaColor}`}>{delta}</span>
            <span className="text-xs text-slate-500">{deltaLabel}</span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const RevenueChart = ({ history }) => {
  const data = (history || []).map((h) => ({ name: h.month, value: h.value || 0 }));
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <Card className="border border-slate-200 shadow-sm" data-testid="overview-revenue-chart">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-bold text-slate-900">Évolution du chiffre d'affaires</h3>
          <Select defaultValue="6m">
            <SelectTrigger className="w-[150px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6m">6 derniers mois</SelectItem>
              <SelectItem value="12m">12 derniers mois</SelectItem>
              <SelectItem value="ytd">Cette année</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-2xl font-bold text-slate-900 leading-tight">{fmtCurrency(total)}</p>
        <p className="text-xs text-slate-500 mb-3">Total</p>
        <div className="h-[230px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gradRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={fmtShort} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                formatter={(v) => [fmtCurrency(v), 'CA']}
                labelFormatter={(l) => `${l} 2026`}
              />
              <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} fill="url(#gradRev)" />
              <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4, fill: '#3b82f6' }} activeDot={{ r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const ContractDistribution = ({ distribution }) => {
  const navigate = useNavigate();
  const d = distribution || { actifs: 0, en_attente: 0, termines: 0, resilies: 0 };
  const total = (d.actifs || 0) + (d.en_attente || 0) + (d.termines || 0) + (d.resilies || 0);
  const items = [
    { key: 'actifs', label: 'Actifs', value: d.actifs, color: '#10b981' },
    { key: 'en_attente', label: 'En attente', value: d.en_attente, color: '#f59e0b' },
    { key: 'termines', label: 'Terminés', value: d.termines, color: '#3b82f6' },
    { key: 'resilies', label: 'Résiliés', value: d.resilies, color: '#ef4444' },
  ];
  const pieData = items.filter((i) => i.value > 0);

  return (
    <Card className="border border-slate-200 shadow-sm" data-testid="overview-contracts-pie">
      <CardContent className="p-5">
        <h3 className="text-base font-bold text-slate-900 mb-3">Répartition des contrats par statut</h3>
        <div className="grid grid-cols-2 gap-4 items-center">
          <div className="relative h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData.length ? pieData : [{ key: 'empty', value: 1, color: '#e2e8f0' }]} dataKey="value" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2} startAngle={90} endAngle={-270}>
                  {(pieData.length ? pieData : [{ color: '#e2e8f0' }]).map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} stroke="#fff" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, _n, p) => [v, p.payload.label]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-3xl font-bold text-slate-900 leading-none">{total}</p>
              <p className="text-xs text-slate-500 mt-1">Total</p>
            </div>
          </div>
          <div className="space-y-2.5">
            {items.map((it) => {
              const pct = total > 0 ? Math.round((it.value / total) * 100) : 0;
              return (
                <div key={it.key} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: it.color }} />
                    <span className="text-slate-600">{it.label}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{it.value} <span className="text-slate-400 font-normal">({pct}%)</span></span>
                </div>
              );
            })}
          </div>
        </div>
        <button
          onClick={() => navigate('/contrats')}
          className="mt-4 w-full text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
          data-testid="overview-see-all-contracts"
        >
          Voir tous les contrats <ArrowRight className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
};

const activityColors = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', icon: FileCheck },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', icon: Send },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', icon: FileText },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600', icon: Receipt },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600', icon: Bell },
  slate: { bg: 'bg-slate-50', text: 'text-slate-600', icon: Activity },
};

const RecentActivity = ({ activity }) => (
  <Card className="border border-slate-200 shadow-sm" data-testid="overview-recent-activity">
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-slate-900">Activité récente</h3>
        <button className="text-sm font-semibold text-blue-600 hover:text-blue-700">Voir tout</button>
      </div>
      <div className="space-y-3">
        {(activity || []).length === 0 && (
          <p className="text-sm text-slate-400 italic">Aucune activité récente</p>
        )}
        {(activity || []).map((a, idx) => {
          const c = activityColors[a.color] || activityColors.slate;
          const Icon = c.icon;
          const dt = fmtDateTime(a.timestamp);
          return (
            <div key={idx} className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${c.bg}`}>
                <Icon className={`h-4 w-4 ${c.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{a.title}</p>
                <p className="text-xs text-slate-500 truncate">{a.description || '—'}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-slate-500">{dt.date}</p>
                <p className="text-xs text-slate-400">{dt.time}</p>
              </div>
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
);

const sourceColors = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ec4899'];

const PerformanceBySource = ({ sources }) => {
  const navigate = useNavigate();
  const data = sources && sources.length ? sources : [
    { source: 'Prospection', amount: 0, percentage: 0 },
    { source: 'Réseaux sociaux', amount: 0, percentage: 0 },
    { source: 'Recommandation', amount: 0, percentage: 0 },
    { source: 'Partenariats', amount: 0, percentage: 0 },
  ];
  const max = Math.max(...data.map((d) => d.amount), 1);
  return (
    <Card className="border border-slate-200 shadow-sm" data-testid="overview-performance-source">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Performance par source</h3>
          <Select defaultValue="6m">
            <SelectTrigger className="w-[150px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6m">6 derniers mois</SelectItem>
              <SelectItem value="12m">12 derniers mois</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-4">
          {data.map((s, idx) => {
            const widthPct = max > 0 ? Math.max((s.amount / max) * 100, 4) : 4;
            return (
              <div key={s.source}>
                <p className="text-sm text-slate-600 mb-1.5">{s.source}</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${widthPct}%`, background: sourceColors[idx % sourceColors.length] }}
                    />
                  </div>
                  <p className="text-sm font-semibold text-slate-900 whitespace-nowrap min-w-[150px] text-right">
                    {fmtCurrency(s.amount)} <span className="text-slate-400 font-normal">({s.percentage || 0}%)</span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <button
          onClick={() => navigate('/entreprise?tab=reports')}
          className="mt-5 w-full text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
        >
          Voir le rapport complet <ArrowRight className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
};

const TopEntreprises = ({ entreprises }) => {
  const navigate = useNavigate();
  const list = entreprises && entreprises.length ? entreprises : [];
  const colorRing = ['bg-emerald-100 text-emerald-600', 'bg-blue-100 text-blue-600', 'bg-amber-100 text-amber-600', 'bg-rose-100 text-rose-600', 'bg-purple-100 text-purple-600'];
  return (
    <Card className="border border-slate-200 shadow-sm" data-testid="overview-top-entreprises">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Top entreprises</h3>
          <Select defaultValue="6m">
            <SelectTrigger className="w-[150px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6m">6 derniers mois</SelectItem>
              <SelectItem value="12m">12 derniers mois</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          {list.length === 0 && (
            <p className="text-sm text-slate-400 italic">Aucune entreprise pour le moment</p>
          )}
          {list.map((e, idx) => (
            <div key={`${e.nom}-${idx}`} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${colorRing[idx % colorRing.length]}`}>
                  {(e.nom || '?').charAt(0).toUpperCase()}
                </div>
                <p className="text-sm font-medium text-slate-900 truncate">{e.nom}</p>
              </div>
              <p className="text-sm font-semibold text-slate-900 whitespace-nowrap">{fmtCurrency(e.montant)}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => navigate('/clients')}
          className="mt-5 w-full text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
        >
          Voir toutes les entreprises <ArrowRight className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
};

const FinancialSummary = ({ summary }) => {
  const navigate = useNavigate();
  const s = summary || { revenus: 0, depenses: 0, benefice_net: 0, marge: 0 };
  return (
    <Card className="border border-slate-200 shadow-sm" data-testid="overview-financial-summary">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-bold text-slate-900">Résumé financier</h3>
          <Select defaultValue="month">
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Ce mois</SelectItem>
              <SelectItem value="year">Cette année</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Revenus</p>
            <p className="text-sm font-bold text-emerald-600">{fmtCurrency(s.revenus)}</p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Dépenses</p>
            <p className="text-sm font-bold text-rose-600">{fmtCurrency(s.depenses)}</p>
          </div>
          <div className="border-t border-slate-200 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Bénéfice Net</p>
              <p className="text-sm font-bold text-emerald-600">{fmtCurrency(s.benefice_net)}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">Marge bénéficiaire</p>
              <p className="text-sm font-bold text-emerald-600">{s.marge || 0}%</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate('/entreprise?tab=finances')}
          className="mt-5 w-full text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
        >
          Voir le rapport financier <ArrowRight className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
};

export default function EntrepriseOverview({ analytics }) {
  const k = analytics?.kpis || {};

  const revPctValue = k.revenue_change_pct ?? 0;
  const revPctColor = revPctValue >= 0 ? 'text-emerald-600' : 'text-rose-600';
  const revPctText = `${revPctValue >= 0 ? '+' : ''}${revPctValue}%`;

  const ctrChange = k.active_contracts_change ?? 0;
  const ctrColor = ctrChange >= 0 ? 'text-emerald-600' : 'text-rose-600';
  const ctrText = `${ctrChange >= 0 ? '+' : ''}${ctrChange}`;

  const empChange = k.employees_change ?? 0;
  const empColor = empChange === 0 ? 'text-slate-500' : empChange > 0 ? 'text-emerald-600' : 'text-rose-600';
  const empText = `${empChange >= 0 ? '+' : ''}${empChange}`;

  const convChange = k.conversion_change_pct ?? 0;
  const convColor = convChange >= 0 ? 'text-emerald-600' : 'text-rose-600';
  const convText = `${convChange >= 0 ? '+' : ''}${convChange}%`;

  return (
    <div className="space-y-5" data-testid="entreprise-overview-tab">
      {/* 4 KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Activity}
          iconBg="bg-blue-50 text-blue-600"
          label="Chiffre d'affaires (ce mois)"
          value={fmtCurrency(k.monthly_revenue || 0)}
          delta={revPctText}
          deltaLabel="vs mois dernier"
          deltaColor={revPctColor}
          testid="kpi-revenue"
        />
        <KpiCard
          icon={CheckCircle}
          iconBg="bg-emerald-50 text-emerald-600"
          label="Contrats actifs"
          value={k.active_contracts ?? 0}
          delta={ctrText}
          deltaLabel="vs mois dernier"
          deltaColor={ctrColor}
          testid="kpi-active-contracts"
        />
        <KpiCard
          icon={Users}
          iconBg="bg-purple-50 text-purple-600"
          label="Employés"
          value={k.total_employees ?? 0}
          delta={empText}
          deltaLabel="vs mois dernier"
          deltaColor={empColor}
          testid="kpi-employees"
        />
        <KpiCard
          icon={TrendingUp}
          iconBg="bg-rose-50 text-rose-600"
          label="Taux de conversion"
          value={`${k.conversion_rate ?? 0}%`}
          delta={convText}
          deltaLabel="vs mois dernier"
          deltaColor={convColor}
          testid="kpi-conversion"
        />
      </div>

      {/* Row 1: 3 cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <RevenueChart history={analytics?.revenue_history} />
        <ContractDistribution distribution={analytics?.contract_distribution} />
        <RecentActivity activity={analytics?.recent_activity} />
      </div>

      {/* Row 2: 3 cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <PerformanceBySource sources={analytics?.performance_by_source} />
        <TopEntreprises entreprises={analytics?.top_entreprises} />
        <FinancialSummary summary={analytics?.financial_summary} />
      </div>
    </div>
  );
}
